import React, { useState } from 'react'
import './EditableDescriptionCell.scss'
import { ReactComponent as ExpandIcon } from '../../assets/icons/chevron-up.svg'
import { ReactComponent as CollapseIcon } from '../../assets/icons/chevron-down.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg'
import Button from '../Button/Button'
import InputTextArea from '../InputTextArea/InputTextArea'
import { Divider } from 'antd'
import { toastNotify } from '../../utils/helperFunctions/toastNotify'
import { TOAST } from '../../utils/constants/constant'
import AutoFieldComponent from '../AutoComplete/AutoComplete'

const EditableDescriptionCell = ({ description, index, handleFieldChange }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedDescription, setEditedDescription] = useState(description)

  const handleToggleExpand = () => {
    if (!isEditing) {
      setIsExpanded(!isExpanded)
    }
  }

  const handleToggleExpandKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggleExpand()
    }
  }

  const handleEditClick = (e) => {
    e.stopPropagation()
    setIsEditing(true)
    setIsExpanded(true)
  }

  const handleSaveClick = (e) => {
    e.stopPropagation()
    handleFieldChange(editedDescription, 'description', index)
    setIsEditing(false)
    setIsExpanded(false)
    toastNotify(TOAST.success, 'Description updated successfully')
  }

  const handleCancelClick = (e) => {
    e.stopPropagation()
    setEditedDescription(description)
    setIsEditing(false)
    setIsExpanded(false)
  }

  // Function to format the description with bold text
  const formatDescription = (text) => {
    if (!text) return 'Click to expand...'

    // Add bold formatting to key parts
    let formattedText = text
      .replace(
        'Software Development Services',
        '<strong style="color: #000; font-weight: 600;">Software Development Services</strong>'
      )
      .replace(
        'Engineer(s):',
        '<strong style="color: #000; font-weight: 600;">Engineer(s):</strong>'
      )
      .replace(
        'Total Cost:',
        '<strong style="color: #000; font-weight: 600;">Total Cost:</strong>'
      )

    return formattedText
  }

  // Render formatted description when not editing
  const renderFormattedDescription = () => {
    if (isEditing) {
      return (
        <InputTextArea
          name="description"
          existingValue={editedDescription}
          placeholder="Write description..."
          onClick={(e) => (isEditing ? e.stopPropagation() : null)}
          onChange={(e) => isEditing && setEditedDescription(e.target.value)}
          required
          minRows={5}
          maxRows={22}
          className={isEditing ? 'edit-mode' : ''}
          disabled={!isEditing}
        />
      )
    }

    return (
      <div
        className="description-content"
        dangerouslySetInnerHTML={{ __html: formatDescription(description) }}
      />
    )
  }

  const renderToggleIcon = () => {
    if (!description) return null

    return isExpanded ? (
      <ExpandIcon
        className="toggle-icon"
        title="Collapse"
        onClick={handleToggleExpand}
      />
    ) : (
      <CollapseIcon
        className="toggle-icon"
        title="Expand"
        onClick={handleToggleExpand}
      />
    )
  }

  return (
    <div className={`description-cell-container ${isEditing ? 'editing' : ''}`}>
      <div className="description-toggle">
        {isExpanded ? (
          <div className="w-100">
            {renderFormattedDescription()}

            {isEditing && (
              <div className="description-button">
                <Button danger label={'Cancel'} onClick={handleCancelClick} />
                <Button
                  label={'Save'}
                  type="primary"
                  onClick={handleSaveClick}
                />
              </div>
            )}
          </div>
        ) : (
          <span
            className="description-text"
            role="button"
            tabIndex={0}
            aria-expanded={false}
            onClick={handleToggleExpand} // ✅ only text triggers expand
            onKeyDown={handleToggleExpandKeyDown}
          >
            {description ? (
              description.substring(0, 80) || 'Add description here...'
            ) : (
              <AutoFieldComponent
                type="text"
                value="Add description here..."
                disabled={!description}
                onClick={handleEditClick}
                style={{
                  color: '#bfbfbf',
                  backgroundColor: '#fff',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  width: '100%',
                }}
              />
            )}
          </span>
        )}
        <div className="icon-container">
          {renderToggleIcon()}
          <Divider type="vertical" />
          <EditIcon
            className="edit-icon"
            title="Edit Description"
            onClick={handleEditClick}
          />
        </div>
      </div>
    </div>
  )
}

export default EditableDescriptionCell
